<template>
  <div class="multi-token-modal-content">
    <UIChevron v-if="data.isOneTimeOffer" :type="ChevronType.PointerLarge">
      <div class="chevron-content">
        <span class="chevron-title">{{ $t('bundleChevronTitle') }}</span>
      </div>
    </UIChevron>

    <div class="image-wrapper">
      <img :src="data.src" />
    </div>

    <span class="subtitle">{{ $t(data.title || '') }}</span>

    <UITokensList :data="data.items" />

    <div class="price-row">
      <span class="price-row-title">{{ $t('corePrice') }}</span>
      <UIPrice :value="data.priceAmount" :label="$t('coreUsdt')" :value-postfix="data.originalPriceAmount" />
    </div>

    <hr />

    <UIBalance
      :token-address="blockchain.contracts.usdt"
      :label="$t('coreYourBalance')"
      :postfix="'USDT'"
      :is-enough-balance="isEnoughBalance"
    />

    <div class="notice">
      <el-checkbox id="agree-cb" v-model="agreeToRules" type="checkbox" class="checkbox" size="large">
        <div v-html="$t('coreBundlesAcknowledge')" />
      </el-checkbox>
    </div>

    <button
      class="btn-primary"
      size="large"
      :disabled="!agreeToRules || buttonLoading || !isEnoughBalance"
      @click="$emit('click')"
    >
      {{ $t('coreBuy') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import useEnvs from '~/composables/useEnvs';
import { ChevronType } from '~/utils/constants/ui';
import type { MultiTokenPackItem } from '~/types/marketplace';

const { blockchain } = useEnvs();

defineEmits(['click']);

defineProps<{
  data: MultiTokenPackItem;
  buttonLoading: boolean;
  isEnoughBalance: boolean;
}>();

const agreeToRules = ref(false);
</script>

<style scoped lang="scss">
.multi-token-modal {
  .multi-token-modal-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    @media screen and (max-width: 479px) {
      padding: 0 10px;
    }

    .image-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      position: relative;

      img {
        margin-top: 13px;
        width: 148px;
        height: 143px;
        max-width: 148px;
        max-height: 143px;
      }
    }

    .chevron-content {
      display: flex;
      gap: 5px;
      align-items: flex-start;
      justify-content: center;
      flex-direction: column;
      height: 100%;
      padding-left: 30px;

      .chevron-title {
        font-family: Eczar;
        font-weight: 600;
        font-size: 16px;
        line-height: 1.2;
        color: var(--main-text-color);
        word-wrap: break-word;
        width: 100px;
      }

      .chevron-description {
        font-family: Eczar;
        font-weight: 400;
        font-size: 14px;
        line-height: 1.2;
        color: var(--dark-9);
      }
    }

    hr {
      background: #3a6e80;
      height: 1px;
      width: 100%;
      margin: 20px 0 0;
      border: none;
    }

    .title {
      font-family: Grenze;
      font-weight: 700;
      font-size: 32px;
      color: var(--main-text-color);
      text-align: center;
    }

    .subtitle {
      font-family: Grenze;
      font-weight: 700;
      font-size: 32px;
      color: var(--main-text-color);
    }

    .price-row {
      display: flex;
      align-items: center;
      width: 100%;
      justify-content: space-between;
      margin-top: 7px;
      margin-bottom: 0;

      .price-row-title {
        font-family: Eczar;
        font-weight: 500;
        font-size: 20px;
        line-height: 1;
        color: var(--second-text-color);
      }
    }

    .tokens-list {
      gap: 9px;
    }

    .notice {
      margin-top: 10px;
      margin-bottom: 20px;
    }

    .chevron-pointer-large {
      position: absolute;
      left: -1px;
      top: 160px;
      transform: translateY(-50%);
      z-index: 10;
    }
  }
}

.multi-token-modal.ru-locale {
  .multi-token-modal-content {
    .chevron-title {
      width: 150px;
    }
  }
}
</style>

<style lang="scss">
.el-dialog.multi-token-modal {
  min-width: 500px;
  max-width: 550px;

  .el-dialog__title {
    line-height: 38px;
  }

  .el-dialog__body {
    padding-top: 10px;
  }

  .tokens-list .tokens-list-row {
    .tokens-list-row-left-side .token-title {
      font-size: 24px;
      font-family: Eczar !important;
    }

    .tokens-list-row-right-side .token-amount {
      font-size: 20px;
    }
  }

  .balance {
    display: flex;
    height: 35px;
    justify-content: space-between;
    width: 100%;
    padding: 0;
    margin-top: 20px;
    box-shadow: none;
    border: none;
    border-radius: unset;
    background-color: transparent;
    flex-wrap: wrap;

    .balance-label {
      font-family: Eczar;
      font-weight: 500;
      font-size: 20px;
      line-height: 1;
      color: var(--second-text-color);
    }

    .price-value {
      font-size: 20px;
      color: var(--green-2);
    }

    .price-value-zero {
      color: var(--canceled-color);
    }

    .price-postfix {
      font-size: 20px;
      font-weight: 600;
      color: inherit;
      text-decoration: none;
    }

    .price-container {
      img {
        height: 21px;
        width: 21px;
      }
    }
  }

  .balance.balance-not-enough {
    .price-value {
      color: var(--canceled-color);
    }
  }

  .price-container {
    .price-content {
      font-family: Eczar;
      font-weight: 600;
      font-size: 24px;
      line-height: 1;
      color: var(--main-text-color);
    }

    img {
      height: 25px;
      width: 30px;
    }
  }

  .el-checkbox.el-checkbox--large .el-checkbox__label {
    font-size: 16px !important;
  }
}

.el-dialog.multi-token-modal.multi-token-modal-width {
  min-width: unset;
}
</style>
